import { useFormContext } from 'react-hook-form';

import { Chip, Grid, MenuItem } from '@mui/material';

import supportedLanguages from 'src/locales/supported-languages.json';

import { Field } from './fields';

const languageOptions = supportedLanguages.map((lang) => ({
  value: lang.code,
  languageName: `${lang.flag} ${lang.label}`,
}));

export interface LanguageSelectionProps {
  communicationLanguagesLabel: string;
  communicationLanguagesHelperText?: string;
  preferredLanguageLabel: string;
  preferredLanguageHelperText?: string;
  renderStyle?: 'chip' | 'basic';
  gridProps?: {
    communicationLanguages?: any;
    preferredLanguage?: any;
  };
}

export function LanguageSelection({
  communicationLanguagesLabel,
  communicationLanguagesHelperText,
  preferredLanguageLabel,
  preferredLanguageHelperText,
  renderStyle = 'chip',
  gridProps,
}: LanguageSelectionProps) {
  const { watch } = useFormContext();

  const communicationLanguages = watch('communicationLanguages');

  const renderTags = (selected: any[], getTagProps: any) => {
    if (renderStyle === 'chip') {
      return selected.map((option, index) => {
        const { key, ...tagProps } = getTagProps({ index });

        return (
          <Chip
            {...tagProps}
            key={key ?? option.value}
            label={option.languageName}
            size="small"
            color="primary"
            variant="soft"
          />
        );
      });
    }

    return selected.map((option, index) => {
      const { key, ...tagProps } = getTagProps({ index });

      return (
        <Chip
          {...tagProps}
          key={key ?? option.value}
          label={option.languageName}
          size="small"
          sx={{
            bgcolor: 'var(--palette-primary-lighter, #e3f2fd)',
            color: 'var(--palette-primary-dark, #1976d2)',
            m: '2px',
            borderRadius: '16px',
            fontSize: '12px',
          }}
        />
      );
    });
  };

  const CommunicationLanguagesField = (
    <Field.Autocomplete
      name="communicationLanguages"
      label={communicationLanguagesLabel}
      helperText={communicationLanguagesHelperText}
      multiple
      options={languageOptions}
      getOptionLabel={(option) => option.languageName}
      isOptionEqualToValue={(option, val) => option.value === val?.value}
      renderTags={renderTags}
      renderOption={(props, option) => (
        <li {...props} key={option.value}>
          {option.languageName}
        </li>
      )}
    />
  );

  const PreferredLanguageField = (
    <Field.Select
      name="preferredLanguage"
      label={preferredLanguageLabel}
      helperText={preferredLanguageHelperText}
    >
      {communicationLanguages?.filter(Boolean).map((lang: any) => (
        <MenuItem key={lang?.value || lang} value={lang?.value || lang}>
          {lang?.languageName || lang}
        </MenuItem>
      )) || languageOptions.map((lang) => (
        <MenuItem key={lang.value} value={lang.value}>
          {lang.languageName}
        </MenuItem>
      ))}
    </Field.Select>
  );

  if (gridProps) {
    return (
      <>
        <Grid item {...gridProps.communicationLanguages}>
          {CommunicationLanguagesField}
        </Grid>
        <Grid item {...gridProps.preferredLanguage}>
          {PreferredLanguageField}
        </Grid>
      </>
    );
  }

  return (
    <>
      {CommunicationLanguagesField}
      {PreferredLanguageField}
    </>
  );
}
